/**
 * SKOP Report Modal
 *
 * Modal form for submitting token reports.
 */

import { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import {
  submitReport,
  hasUserReported,
  type ReportReason,
  getReasonLabel,
  getReasonColor,
} from '../lib/reports';

interface ReportModalProps {
  tokenAddress: string;
  tokenSymbol: string;
  tokenName: string;
  onClose: () => void;
  onSubmit?: () => void;
}

const REASONS: ReportReason[] = ['rug_pull', 'honeypot', 'fake_team', 'scam', 'other'];

export function ReportModal({
  tokenAddress,
  tokenSymbol,
  tokenName,
  onClose,
  onSubmit,
}: ReportModalProps) {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toString() ?? null;

  const [reason, setReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [evidenceLinks, setEvidenceLinks] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const alreadyReported = walletAddress ? hasUserReported(walletAddress, tokenAddress) : false;

  const handleAddLink = () => {
    setEvidenceLinks([...evidenceLinks, '']);
  };

  const handleLinkChange = (index: number, value: string) => {
    const newLinks = [...evidenceLinks];
    newLinks[index] = value;
    setEvidenceLinks(newLinks);
  };

  const handleRemoveLink = (index: number) => {
    setEvidenceLinks(evidenceLinks.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!walletAddress) {
      setError('Please connect your wallet to submit a report');
      return;
    }

    if (!reason) {
      setError('Please select a reason for your report');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const filteredLinks = evidenceLinks.filter(l => l.trim() !== '');

      submitReport(walletAddress, {
        tokenAddress,
        tokenSymbol,
        tokenName,
        reason,
        description: description.trim() || undefined,
        evidenceLinks: filteredLinks.length > 0 ? filteredLinks : undefined,
      });

      setSuccess(true);
      onSubmit?.();

      // Close after brief success message
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          maxWidth: '420px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700 }}>
            Report Token
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              background: 'none',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '18px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        {/* Token Info */}
        <div style={{
          padding: '12px',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          marginBottom: '20px',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tokenName}</div>
          <div style={{
            fontSize: '12px',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
          }}>
            {tokenAddress.slice(0, 8)}...{tokenAddress.slice(-8)}
          </div>
        </div>

        {alreadyReported ? (
          <div style={{
            padding: '16px',
            background: 'rgba(255, 184, 0, 0.1)',
            border: '1px solid rgba(255, 184, 0, 0.3)',
            borderRadius: 'var(--radius-md)',
            textAlign: 'center',
          }}>
            <div style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: 'var(--success)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
            </div>
            <div style={{ color: '#ffb800', fontWeight: 600 }}>
              You've already reported this token
            </div>
          </div>
        ) : success ? (
          <div style={{
            padding: '16px',
            background: 'rgba(0, 255, 136, 0.1)',
            border: '1px solid rgba(0, 255, 136, 0.3)',
            borderRadius: 'var(--radius-md)',
            textAlign: 'center',
          }}>
            <div style={{ width: '24px', height: '24px', margin: '0 auto 8px', color: 'var(--success)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
            </div>
            <div style={{ color: '#00ff88', fontWeight: 600 }}>
              Report submitted successfully
            </div>
          </div>
        ) : (
          <>
            {/* Reason Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                marginBottom: '8px',
              }}>
                Reason for report *
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReason(r)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: reason === r ? `${getReasonColor(r)}15` : 'var(--bg-tertiary)',
                      color: reason === r ? getReasonColor(r) : 'var(--text-secondary)',
                      border: `1px solid ${reason === r ? getReasonColor(r) : 'var(--border)'}`,
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {getReasonLabel(r)}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                marginBottom: '8px',
              }}>
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide details about what happened..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '13px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  resize: 'vertical',
                }}
              />
            </div>

            {/* Evidence Links */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: 'var(--text-tertiary)',
                marginBottom: '8px',
              }}>
                Evidence links (optional)
              </label>
              {evidenceLinks.map((link, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleLinkChange(index, e.target.value)}
                    placeholder="https://solscan.io/tx/..."
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      background: 'var(--bg-tertiary)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  {evidenceLinks.length > 1 && (
                    <button
                      onClick={() => handleRemoveLink(index)}
                      style={{
                        padding: '8px',
                        background: 'none',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--error)',
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {evidenceLinks.length < 5 && (
                <button
                  onClick={handleAddLink}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    background: 'none',
                    border: '1px dashed var(--border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  + Add Link
                </button>
              )}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                padding: '12px',
                background: 'rgba(255, 59, 59, 0.1)',
                border: '1px solid rgba(255, 59, 59, 0.3)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--error)',
                fontSize: '13px',
                marginBottom: '20px',
              }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={onClose}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !reason || !walletAddress}
                className="btn btn-primary"
                style={{
                  flex: 1,
                  opacity: submitting || !reason || !walletAddress ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {submitting ? (
                  <>
                    <div
                      className="animate-spin"
                      style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid rgba(0, 0, 0, 0.2)',
                        borderTopColor: '#000',
                        borderRadius: '50%',
                      }}
                    />
                    Submitting...
                  </>
                ) : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
